import { Box, styled } from '@mui/material';
import { formatDistanceToNow } from 'date-fns';
import { JobStatus, Report } from '@/api/generatedApi';
import { BodyText } from '@/components/BodyText';
import { FailureWhyButton } from '@/components/FailureDiagnostics/FailureWhyButton';
import { ReportDetailsCell } from '@/components/Report/ReportDetailsCell';
import { ReportTypeIcon } from '@/components/Report/ReportTypeIcon';
import { StatusBadge } from '@/components/StatusBadge';
import { REPORT_TITLES } from '@/components/Report/consts';
import { getReportCreatorLabel } from '@/components/Report/getReportCreatorLabel';
import { useTenants } from '@/services/tenants/useTenants';
import { CreatedCell, CreatorCell, EllipsisText, Row, RowLink, StatusCell, WhyControl } from './RecentRow';

// Same columns and order as the reports page grid. The ratios are not the grid's: this tile is a
// third narrower, and the grid's generous details column truncated every name and date here, so
// details yields space to the rest.
export const NameCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  flex: 2,
  minWidth: 0,
  // The reports page sizes its type icons at 18px.
  '& svg': {
    fontSize: '18px',
  },
});

export const DetailsCell = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  flex: 1.5,
  minWidth: 0,
  overflow: 'hidden',
});

export const GroupCountCell = styled(BodyText)({
  flexShrink: 0,
  width: '52px',
  textAlign: 'right',
  fontVariantNumeric: 'tabular-nums',
});

export const RecentReportRow = ({ report }: { report: Report }) => {
  const { getPath } = useTenants();
  const name = report.name || REPORT_TITLES[report.reportType];

  return (
    <Row data-testid='recent-report-row'>
      <RowLink to={getPath(`report/${report.id}`)} title={`View ${name}`}>
        <NameCell>
          <ReportTypeIcon reportType={report.reportType} />
          <EllipsisText>{name}</EllipsisText>
        </NameCell>
        <DetailsCell>
          <ReportDetailsCell report={report} noWrap />
        </DetailsCell>
        <GroupCountCell color='text.secondary' data-testid='report-group-count'>
          {report.groupCount}
        </GroupCountCell>
        <CreatorCell color='text.secondary'>{getReportCreatorLabel(report)}</CreatorCell>
        <CreatedCell color='text.secondary'>
          {formatDistanceToNow(new Date(report.createdAt), { addSuffix: true })}
        </CreatedCell>
      </RowLink>
      <StatusCell>
        <StatusBadge status={report.status} progress={report.progress} data-testid={`report-status-${report.status}`} />
        {report.status === JobStatus.FAILED && (
          <WhyControl>
            <FailureWhyButton assetId={report.id} assetName={name} kind='report' />
          </WhyControl>
        )}
      </StatusCell>
    </Row>
  );
};
